/**
 * Custom product gallery: horizontal scroll track + thumbnails.
 * Exposes window.CustomGallery.init(element) for bundle proposal media.
 */
(function () {
  'use strict';

  function bindGallery(gallery) {
    var track = gallery.querySelector('.custom-gallery__slides');
    if (!track) {
      return function () {};
    }

    var slides = Array.from(gallery.querySelectorAll('.custom-gallery__slide'));
    var thumbItems = Array.from(gallery.querySelectorAll('.custom-gallery__thumb-item'));
    var thumbBtns = Array.from(gallery.querySelectorAll('.custom-gallery__thumb-btn'));
    var thumbList = gallery.querySelector('.custom-gallery__thumbnails');
    var prevBtn = gallery.querySelector('.custom-gallery__arrow--prev');
    var nextBtn = gallery.querySelector('.custom-gallery__arrow--next');

    var total = slides.length;
    if (total === 0) {
      return function () {};
    }

    var current = slides.findIndex(function (s) {
      return s.classList.contains('is-active');
    });
    if (current < 0) current = 0;

    var prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var scrollBehavior = prefersReduce ? 'auto' : 'smooth';
    var programmatic = false;
    var scrollSyncTimer;
    var ro = null;

    var disposers = [];

    function addListener(el, type, fn, options) {
      if (!el || typeof el.addEventListener !== 'function') return;
      el.addEventListener(type, fn, options);
      disposers.push(function () {
        el.removeEventListener(type, fn);
      });
    }

    function slideWidth() {
      return track.clientWidth;
    }

    function setSlideWidths() {
      var w = slideWidth();
      if (w > 0) track.style.setProperty('--cg-slide-w', w + 'px');
    }

    function indexFromScroll() {
      var w = slideWidth();
      if (!w) return 0;
      var idx = Math.round(track.scrollLeft / w);
      return Math.max(0, Math.min(total - 1, idx));
    }

    function applyActive(index) {
      index = Math.max(0, Math.min(total - 1, index));
      var prev = current;

      if (prev !== index) {
        var leavingVideo = slides[prev] && slides[prev].querySelector('video');
        if (leavingVideo) leavingVideo.pause();
      }

      slides.forEach(function (slide, i) {
        var on = i === index;
        slide.classList.toggle('is-active', on);
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      });

      thumbItems.forEach(function (item, i) {
        item.classList.toggle('is-active', i === index);
      });
      thumbBtns.forEach(function (btn, i) {
        if (i === index) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });

      current = index;

      if (thumbItems[index] && thumbList) {
        var item = thumbItems[index];
        var scrollTarget = item.offsetLeft - thumbList.clientWidth / 2 + item.clientWidth / 2;
        thumbList.scrollTo({ left: scrollTarget, behavior: 'smooth' });
      }
    }

    function syncFromScroll() {
      applyActive(indexFromScroll());
    }

    function scheduleSyncFromScroll() {
      clearTimeout(scrollSyncTimer);
      scrollSyncTimer = setTimeout(syncFromScroll, 60);
    }

    function goTo(index) {
      index = ((index % total) + total) % total;
      var w = slideWidth();
      if (w <= 0) return;

      if (index === current && Math.abs(track.scrollLeft - index * w) < 4) return;

      programmatic = true;
      track.scrollTo({ left: index * w, behavior: scrollBehavior });
      applyActive(index);

      setTimeout(function () {
        programmatic = false;
      }, scrollBehavior === 'auto' ? 0 : 600);
    }

    function onScroll() {
      if (!programmatic) scheduleSyncFromScroll();
    }

    function onScrollEnd() {
      programmatic = false;
      syncFromScroll();
    }

    function onWinResize() {
      setSlideWidths();
      var w = slideWidth();
      if (w > 0) track.scrollTo({ left: current * w, behavior: 'auto' });
    }

    function alignScrollToCurrent(attemptsLeft) {
      setSlideWidths();
      var w = slideWidth();
      if (w > 0) {
        track.scrollLeft = current * w;
        return;
      }
      if (attemptsLeft > 0) {
        requestAnimationFrame(function () {
          alignScrollToCurrent(attemptsLeft - 1);
        });
      }
    }

    setSlideWidths();
    requestAnimationFrame(function () {
      alignScrollToCurrent(12);
    });
    setTimeout(function () {
      setSlideWidths();
      var w = slideWidth();
      if (w > 0) track.scrollLeft = current * w;
    }, 0);

    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(function () {
        setSlideWidths();
        var w = slideWidth();
        if (w > 0) track.scrollTo({ left: current * w, behavior: 'auto' });
      });
      ro.observe(track);
    } else {
      addListener(window, 'resize', onWinResize);
    }

    addListener(track, 'scroll', onScroll, { passive: true });
    addListener(track, 'scrollend', onScrollEnd);

    if (prevBtn) {
      addListener(prevBtn, 'click', function () {
        goTo(current - 1);
      });
    }
    if (nextBtn) {
      addListener(nextBtn, 'click', function () {
        goTo(current + 1);
      });
    }

    thumbBtns.forEach(function (btn, i) {
      addListener(btn, 'click', function () {
        goTo(i);
      });
    });
    thumbItems.forEach(function (item, i) {
      addListener(item, 'keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goTo(i);
        }
      });
    });

    return function destroy() {
      clearTimeout(scrollSyncTimer);
      disposers.forEach(function (d) {
        d();
      });
      disposers.length = 0;
      if (ro) {
        ro.disconnect();
        ro = null;
      }
    };
  }

  function resolveEl(el) {
    if (!el) return null;
    if (el.nodeType === 1) return el;
    if (typeof el === 'string') return document.querySelector(el);
    return null;
  }

  window.CustomGallery = {
    init: function (gallery) {
      var el = resolveEl(gallery);
      if (!el || !el.classList || !el.classList.contains('custom-gallery')) return;

      if (el._cgDestroy) {
        el._cgDestroy();
        el._cgDestroy = null;
      }

      el.removeAttribute('data-ig-inited');
      el._cgDestroy = bindGallery(el);
    },

    destroy: function (gallery) {
      var el = resolveEl(gallery);
      if (!el || !el._cgDestroy) return;
      el._cgDestroy();
      el._cgDestroy = null;
    },

    reinit: function (gallery) {
      this.init(gallery);
    },

    initAll: function (root) {
      var scope = root && root.querySelectorAll ? root : document;
      var nodes = scope.querySelectorAll('.custom-gallery');
      for (var i = 0; i < nodes.length; i++) {
        this.init(nodes[i]);
      }
    },

    reinitAll: function (root) {
      this.initAll(root);
    },

    bindWhenReady: function (gallery, options) {
      var self = this;
      var maxAttempts = (options && options.maxAttempts) || 150;
      var intervalMs = (options && options.intervalMs) || 50;
      var attempts = 0;

      function tryBind() {
        var el = resolveEl(gallery);
        if (!el || !el.classList || !el.classList.contains('custom-gallery')) return false;
        self.init(el);
        return true;
      }

      if (tryBind()) return true;

      var id = setInterval(function () {
        attempts++;
        if (tryBind() || attempts >= maxAttempts) clearInterval(id);
      }, intervalMs);

      return false;
    },
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('custom-gallery:attach', function (ev) {
      var d = ev && ev.detail;
      var el = d && (d.gallery || d.element || d.node);
      if (!el || !window.CustomGallery) return;
      window.CustomGallery.init(el);
    });
  }
})();
